'use client'

import React, { createContext, useContext, useState, ReactNode } from 'react'
import { ROI_CONVERSATION_LIMITS } from '@/config/pricing'

type PlanTier = 'basic' | 'professional' | 'enterprise'

interface PricingContextType {
  // ROI State
  conversations: number
  setConversations: (val: number) => void
  timePerConv: number
  setTimePerConv: (val: number) => void
  hourlyRate: number
  setHourlyRate: (val: number) => void

  // Calculated Values
  recommendedPlan: PlanTier
  monthlySavings: number
  yearlySavings: number
  hoursSaved: number

  // UI State
  billingPeriod: 'monthly' | 'yearly'
  setBillingPeriod: (period: 'monthly' | 'yearly') => void

  // Actions
  scrollToPricing: () => void
}

const PricingContext = createContext<PricingContextType | undefined>(undefined)

export function PricingProvider({ children }: { children: ReactNode }) {
  const [conversations, setConversations] = useState(3000)
  const [timePerConv, setTimePerConv] = useState(10) // minutes
  const [hourlyRate, setHourlyRate] = useState(15) // USD/hr
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly')

  // Derived State (Calculation Logic)
  // 1. Determine Recommended Plan
  let recommendedPlan: PlanTier = 'enterprise'
  if (conversations <= ROI_CONVERSATION_LIMITS.basic) {
    recommendedPlan = 'basic'
  } else if (conversations <= ROI_CONVERSATION_LIMITS.professional) {
    recommendedPlan = 'professional'
  }

  // 2. Calculate Savings
  const humanHours = (conversations * timePerConv) / 60
  const humanCost = humanHours * hourlyRate

  // Get plan cost based on current selection
  const basicCost = billingPeriod === 'yearly' ? 39 : 49
  const proCost = billingPeriod === 'yearly' ? 71 : 89
  const enterpriseEst = 299 // Estimate

  let luckAgentsCost = 0
  if (conversations <= ROI_CONVERSATION_LIMITS.basic) luckAgentsCost = basicCost
  else if (conversations <= ROI_CONVERSATION_LIMITS.professional) luckAgentsCost = proCost
  else luckAgentsCost = enterpriseEst

  const monthlySavings = Math.max(0, humanCost - luckAgentsCost)
  const yearlySavings = monthlySavings * 12
  const hoursSaved = humanHours

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('precios')
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <PricingContext.Provider
      value={{
        conversations,
        setConversations,
        timePerConv,
        setTimePerConv,
        hourlyRate,
        setHourlyRate,
        recommendedPlan,
        monthlySavings,
        yearlySavings,
        hoursSaved,
        billingPeriod,
        setBillingPeriod,
        scrollToPricing,
      }}
    >
      {children}
    </PricingContext.Provider>
  )
}

export function usePricing() {
  const context = useContext(PricingContext)
  if (context === undefined) {
    throw new Error('usePricing must be used within a PricingProvider')
  }
  return context
}
